import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const categories = [
  { title: "Electrician", icon: "⚡" },
  { title: "Plumber", icon: "🔧" },
  { title: "Driver", icon: "🚗" },
  { title: "Maid", icon: "👩🏻‍⚖️" },
  { title: "Mason", icon: "🧱" },
  { title: "Painter", icon: "🎨" },
  { title: "Guard", icon: "🛡️" },
  { title: "Delivery", icon: "📦" },
];

const FindWork = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch("https://digital-worker-exchange.onrender.com/api/jobs");
        if (!res.ok) throw new Error("Failed to fetch jobs");

        const data = await res.json();
        setJobs(data);
        setFilteredJobs(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleSearch = () => {
    let filtered = jobs;

    if (search.trim()) {
      filtered = filtered.filter((job) =>
        job.title.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (category) {
      filtered = filtered.filter((job) =>
        job.trade?.toLowerCase().includes(category.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  useEffect(() => {
    handleSearch();
  }, [category]);

  if (error) return <Error error={error} />;
  if (jobs.length === 0 && !loading) return <Empty />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">

      {/* HERO */}
      <section className="pt-24 pb-12 px-6 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-extrabold text-gray-900"
        >
          Find Work Faster.{" "}
          <span className="bg-gradient-to-r from-blue-600 to-emerald-500 bg-clip-text text-transparent">
            Hire Smarter.
          </span>
        </motion.h1>

        <p className="text-gray-600 mt-4 max-w-xl mx-auto">
          Discover verified jobs near you with clear pay, trusted employers, and instant connections.
        </p>

        {/* SEARCH */}
        <div className="mt-8 max-w-xl mx-auto flex bg-white rounded-2xl shadow-lg border overflow-hidden">
          <input
            type="text"
            placeholder="Search jobs (driver, plumber...)"
            className="flex-1 px-4 py-3 outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            onClick={handleSearch}
            className="px-6 bg-gradient-to-r from-blue-600 to-emerald-500 text-white font-semibold"
          >
            Search
          </button>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="px-6">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-3 justify-center">
          {categories.map((cat, i) => (
            <div
              key={i}
              onClick={() =>
                setCategory(category === cat.title ? "" : cat.title)
              }
              className={`px-4 py-2 rounded-full cursor-pointer text-sm transition ${
                category === cat.title
                  ? "bg-gradient-to-r from-blue-600 to-emerald-500 text-white shadow"
                  : "bg-white border hover:shadow"
              }`}
            >
              {cat.icon} {cat.title}
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="mt-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-3 gap-4 text-center">
          <StatCard title={`${jobs.length}+`} subtitle="Active Jobs" />
          <StatCard title="100%" subtitle="Verified Employers" />
          <StatCard title="Daily" subtitle="Payments" />
        </div>
      </section>

      {/* JOB LIST */}
      <section className="mt-14 px-6 pb-16">
        <div className="max-w-6xl mx-auto">

          <div className="flex justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Available Jobs
            </h2>
            <span className="text-gray-500 text-sm">
              {filteredJobs.length} results
            </span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* 🔥 ONLY CHANGE: SKELETON */}
            {loading
              ? Array(6).fill(0).map((_, i) => <SkeletonCard key={i} />)
              : filteredJobs.map((job, i) => (
                  <motion.div
                    key={job._id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white rounded-2xl p-6 border shadow-sm hover:shadow-xl hover:-translate-y-1 transition"
                  >
                    <div className="flex justify-between">
                      <p className="text-emerald-600 font-bold text-lg">
                        ₹{job.minPay} – ₹{job.maxPay}
                      </p>
                      <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                        Hiring
                      </span>
                    </div>

                    <h3 className="mt-3 text-lg font-semibold text-gray-900">
                      {job.title}
                    </h3>

                    <div className="text-gray-600 text-sm mt-2 space-y-1">
                      <p>📍 {job.location}</p>
                      <p>👷 {job.trade}</p>
                      <p>🏢 {job.employer?.name || "Employer"}</p>
                    </div>

                    <Link to={`/job/${job._id}`}>
                      <button className="mt-5 w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 text-white font-semibold hover:opacity-90">
                        View & Apply
                      </button>
                    </Link>
                  </motion.div>
                ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* 🔥 SKELETON CARD (MATCHES YOUR UI EXACTLY) */
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl p-6 border shadow-sm animate-pulse">
    <div className="flex justify-between mb-3">
      <div className="h-5 w-24 bg-gray-200 rounded"></div>
      <div className="h-4 w-12 bg-gray-200 rounded"></div>
    </div>

    <div className="h-6 w-40 bg-gray-200 rounded mb-3"></div>

    <div className="space-y-2 mb-4">
      <div className="h-4 w-32 bg-gray-200 rounded"></div>
      <div className="h-4 w-28 bg-gray-200 rounded"></div>
      <div className="h-4 w-36 bg-gray-200 rounded"></div>
    </div>

    <div className="h-10 w-full bg-gray-200 rounded"></div>
  </div>
);

/* SAME COMPONENTS */
const StatCard = ({ title, subtitle }) => (
  <div className="bg-white rounded-2xl p-4 border shadow-sm">
    <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    <p className="text-gray-500 text-sm">{subtitle}</p>
  </div>
);

const Error = ({ error }) => (
  <div className="min-h-screen flex items-center justify-center text-red-500">
    {error}
  </div>
);

const Empty = () => (
  <div className="min-h-screen flex items-center justify-center text-gray-500">
    No jobs available
  </div>
);

export default FindWork;